import { useEffect, useState } from 'preact/hooks';
import { isAddress } from 'viem';
import type { DeviceInfo } from '../../shared/types';
import { listDevices, registerPasskey } from '../lib/webauthn-client';
import { hasPassphrase, prfWrapperFor, type WalletEntry } from '../lib/vault';
import {
  addPassphrase, addPasskeyWrapper, createVault, forgetVault, loadEnvelope, lockVault,
  saveAddresses, unlockWithPass, unlockWithPasskey, useVault,
} from '../lib/vault-store';
import '../styles/components/dashboard.css';
import { TopBar } from '../components/TopBar';
import '../styles/components/settings.css';

export function SettingsPage() {
  const vault = useVault();
  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    loadEnvelope().catch((e: Error) => setError(e.message));
    listDevices().then(setDevices).catch((e: Error) => setError(e.message));
  }, []);

  const run = (label: string, fn: () => Promise<unknown>) => async () => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(label);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <main class="dash settings">
      <TopBar status="Settings" />
      {error && <div class="banner banner--critical" role="alert">{error}</div>}
      {notice && <div class="banner" role="status">{notice}</div>}

      <section class="panel">
        <h2 class="panel-title">Wallet vault</h2>
        <p class="dim">
          Addresses are encrypted in this browser with a key that only your passkey (or a passphrase) can unwrap.
          The server stores an opaque blob and never sees an address.
        </p>
        {vault.status === 'loading' && <p class="dim">…</p>}
        {vault.status === 'none' && <CreateVault onError={setError} />}
        {vault.status === 'locked' && <Unlock onError={setError} />}
        {vault.status === 'unlocked' && vault.envelope && (
          <>
            <AddressEditor initial={vault.addresses} onSave={(a) => run('Addresses saved', () => saveAddresses(a))()} />
            <div class="row wrap">
              <button class="btn btn--ghost" onClick={lockVault}>Lock</button>
              <AddPassphrase existing={hasPassphrase(vault.envelope)} onDone={(p) => run('Passphrase saved', () => addPassphrase(p))()} />
              <button class="btn btn--ghost" onClick={run('Passkey can now unlock the vault', addPasskeyWrapper)}>Add this passkey as an unlock method</button>
              <button
                class="btn btn--danger"
                onClick={() => { if (confirm('Delete the encrypted vault? Your addresses will have to be re-entered.')) void run('Vault deleted', forgetVault)(); }}
              >
                Delete vault
              </button>
            </div>
          </>
        )}
      </section>

      <section class="panel">
        <h2 class="panel-title">Passkeys</h2>
        <ul class="device-list mono">
          {devices.map((d) => (
            <li key={d.credentialID}>
              <span>{d.deviceName}</span>
              <span class="dim">{new Date(d.createdAt).toLocaleDateString()}</span>
              <span class={`pill ${d.prfEnabled ? 'pill--ok' : ''}`}>{d.prfEnabled ? 'PRF' : 'no PRF'}</span>
              {vault.envelope && <span class={`pill ${prfWrapperFor(vault.envelope, d.credentialID) ? 'pill--ok' : ''}`}>{prfWrapperFor(vault.envelope, d.credentialID) ? 'unlocks vault' : 'no vault key'}</span>}
            </li>
          ))}
        </ul>
        <button class="btn btn--ghost" onClick={run('Passkey registered', async () => { await registerPasskey(); setDevices(await listDevices()); })}>Register another passkey</button>
        <p class="dim small">A passkey without PRF cannot unlock the vault; use a passphrase on that device.</p>
      </section>
    </main>
  );
}

function AddressEditor({ initial, onSave }: { initial: WalletEntry[]; onSave: (a: WalletEntry[]) => void }) {
  const [rows, setRows] = useState<WalletEntry[]>(initial.length ? initial : [{ label: '', address: '' }]);
  const update = (i: number, patch: Partial<WalletEntry>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const valid = rows.every((r) => r.label.trim() && isAddress(r.address));
  return (
    <div class="editor">
      {rows.map((r, i) => (
        <div key={i} class="row">
          <input class="input" placeholder="Label (e.g. cold 1)" value={r.label} onInput={(e) => update(i, { label: (e.target as HTMLInputElement).value })} />
          <input class={`input mono grow${r.address && !isAddress(r.address) ? ' input--bad' : ''}`} placeholder="0x…" value={r.address} onInput={(e) => update(i, { address: (e.target as HTMLInputElement).value.trim() })} />
          <button class="btn btn--ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove">×</button>
        </div>
      ))}
      <div class="row">
        <button class="btn btn--ghost" onClick={() => setRows([...rows, { label: '', address: '' }])}>+ Address</button>
        <button class="btn" disabled={!valid || rows.length === 0} onClick={() => onSave(rows.map((r) => ({ label: r.label.trim(), address: r.address })))}>Save</button>
      </div>
    </div>
  );
}

function CreateVault({ onError }: { onError: (m: string | null) => void }) {
  const [rows, setRows] = useState<WalletEntry[] | null>(null);
  const [passkey, setPasskey] = useState(true);
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  if (!rows) return <AddressEditor initial={[]} onSave={setRows} />;
  const create = async () => {
    setBusy(true);
    onError(null);
    try { await createVault(rows, { passkey, passphrase: pass || undefined }); } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div class="editor">
      <p>{rows.length} address{rows.length > 1 ? 'es' : ''} ready. Choose how to unlock:</p>
      <label class="row"><input type="checkbox" checked={passkey} onChange={(e) => setPasskey((e.target as HTMLInputElement).checked)} /> This passkey (PRF)</label>
      <input class="input" type="password" placeholder="Optional passphrase (fallback for devices without PRF)" value={pass} onInput={(e) => setPass((e.target as HTMLInputElement).value)} />
      <div class="row">
        <button class="btn btn--ghost" onClick={() => setRows(null)}>Back</button>
        <button class="btn" disabled={busy || (!passkey && pass.length < 12)} onClick={create}>{busy ? 'Encrypting…' : 'Create vault'}</button>
      </div>
      {!passkey && pass.length < 12 && <p class="dim small">Passphrase needs at least 12 characters.</p>}
    </div>
  );
}

function Unlock({ onError }: { onError: (m: string | null) => void }) {
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  const go = (fn: () => Promise<void>) => async () => {
    setBusy(true);
    onError(null);
    try { await fn(); } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div class="row wrap">
      <button class="btn" disabled={busy} onClick={go(unlockWithPasskey)}>Unlock with passkey</button>
      <input class="input" type="password" placeholder="Passphrase" value={pass} onInput={(e) => setPass((e.target as HTMLInputElement).value)} />
      <button class="btn btn--ghost" disabled={busy || !pass} onClick={go(() => unlockWithPass(pass))}>Unlock with passphrase</button>
    </div>
  );
}

function AddPassphrase({ existing, onDone }: { existing: boolean; onDone: (p: string) => void }) {
  const [open, setOpen] = useState(false);
  const [pass, setPass] = useState('');
  if (!open) return <button class="btn btn--ghost" onClick={() => setOpen(true)}>{existing ? 'Change passphrase' : 'Add passphrase'}</button>;
  return (
    <span class="row">
      <input class="input" type="password" placeholder="New passphrase (12+ chars)" value={pass} onInput={(e) => setPass((e.target as HTMLInputElement).value)} />
      <button class="btn" disabled={pass.length < 12} onClick={() => { onDone(pass); setOpen(false); setPass(''); }}>Save</button>
    </span>
  );
}
